<script setup lang="ts">
import apiClient, { logout } from '@/api/axios';
import { InternetFreigabeRecht } from '@/api/model';
import { ApiRouten } from '@/api/routen';
import FilialAuswahl from '@/components/FilialAuswahl.vue';
import { useLayout } from '@/layout/composables/layout';
import { RouterHelpers } from '@/router/routeHelpers';
import { useKundenbereichStore } from '@/stores/kundenbereichStore';
import { appConfig } from '@/utils/appConfig';
import { spracheWechseln, t, verfuegbareSprachen } from '@/utils/i18n';
import { getAbsoluteSpaUrl } from '@/utils/pathUtils';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AppBreadcrumbs from './AppBreadcrumbs.vue';

const { toggleMenu, toggleDarkMode, isDarkTheme } = useLayout();
const route = useRoute();
const router = useRouter();

const kunde = route.params.kunde as string | undefined;
const probenApiRouten = ApiRouten.proben(kunde);
const gutachtenApiRouten = ApiRouten.gutachten(kunde);

const kundenbereichStore = useKundenbereichStore();

// Schnellsuche
const suchText = ref<string>('');

/**
 * Führt die Schnellsuche aus und navigiert zur Schnellsuche-Route
 */
const schnellsuche = async () => {
    const eingabe = suchText.value.trim();
    if (!eingabe) {
        return;
    }

    const kunde = route.params.kunde as string | undefined;
    await router.push(RouterHelpers.toSchnellsuche(eingabe, kunde));
    suchText.value = '';
};

/**
 * Behandelt Enter-Taste im Suchfeld
 */
const handleSuchEnter = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
        schnellsuche();
    }
};

// Referenz für das Menü
const menu = ref(null);

// Erstelle das Modell für das Profil-Menü
const profileMenuItems = computed(() => {
    return [
        {
            label: t('[AppTopbar]Kundenbereich'),
            icon: 'pi pi-cog',
            command: () => navigiereZuKundenbereich()
        },
        {
            label: t('[AppTopbar]Hilfe'),
            icon: 'pi pi-question',
            command: () => navigiereZuHilfe()
        },
        // {
        //     label: isDarkTheme ? 'Light Mode' : 'Dark Mode',
        //     icon: isDarkTheme ? 'pi pi-sun' : 'pi pi-moon',
        //     command: () => toggleDarkMode()
        // },
        {
            separator: true
        },
        {
            label: t('[AppTopbar]Logout'),
            icon: 'pi pi-sign-out',
            command: () => handleLogout()
        }
    ];
});

// Funktion zum Öffnen des Menüs
const toggleProfileMenu = (event) => {
    menu.value.toggle(event);
};

/**
 * Handle user logout
 * Checks for unapproved samples and reports before logging out
 */
const handleLogout = async () => {
    // Hier tritt manchmal ein ominöser "NetworkError" auf, siehe https://bav-institut-gmbh.sentry.io/issues/98971073/events/2ee791cee1da4ce2b25a752a76c2756c/
    // Nicht ganz klar, was hier schief geht. Wir dürfen ihn aber nicht verschlucken, da sonst der Logout-Flow im axios-Handler unterbunden wird.
    // Deshalb leben wir erstmal damit
    const anzahlProbenRequest = kundenbereichStore.einstellungen.rechte[InternetFreigabeRecht.ProbenFreigabe] ? apiClient.get<number>(probenApiRouten.anzahlNichtFreigegebeneProben()) : Promise.resolve({ data: 0 });
    const anzahlGutachtenRequest = kundenbereichStore.einstellungen.rechte[InternetFreigabeRecht.GutachtenVersionenErgebnisse] ? apiClient.get<number>(gutachtenApiRouten.anzahlNichtFreigegebeneGutachten()) : Promise.resolve({ data: 0 });
    const [probenResponse, gutachtenResponse] = await Promise.all([anzahlProbenRequest, anzahlGutachtenRequest]);

    if (probenResponse.data > 0 || gutachtenResponse.data > 0) {
        await router.push(RouterHelpers.toNichtFreigegebeneProbenWarnung(probenResponse.data, gutachtenResponse.data, kunde));
        return;
    }

    await logout();
};

/**
 * Navigiert zur Einstellungen-Seite
 */
const navigiereZuKundenbereich = () => {
    const kunde = route.params.kunde as string | undefined;
    router.push(RouterHelpers.toKundenbereich(kunde));
};

const navigiereZuHilfe = () => {
    const kunde = route.params.kunde as string | undefined;
    router.push(RouterHelpers.toHilfe(kunde));
};
</script>

<template>
    <Menubar class="layout-topbar w-full print:!hidden" style="border: none; border-radius: initial">
        <!-- Linke Seite: Breadcrumbs -->
        <template #start>
            <!-- TESTSYSTEM WARNUNG -->
            <div v-if="appConfig.istTestsystem" class="absolute top-0 left-0 w-full flex justify-center pointer-events-none" style="z-index: 9999">
                <div class="bg-red-600 text-white px-6 py-1 rounded-b-lg font-bold shadow-lg text-sm uppercase tracking-wider flex items-center gap-2">
                    <i class="pi pi-exclamation-triangle"></i>
                    Testsystem
                    <i class="pi pi-exclamation-triangle"></i>
                </div>
            </div>

            <button class="layout-menu-button layout-topbar-action mobile-only mr-2" @click="toggleMenu">
                <i class="pi pi-bars"></i>
            </button>

            <!-- Breadcrumbs -->
            <div class="hidden md:flex items-center">
                <AppBreadcrumbs :seite-titel-aktualisieren="true" />
            </div>
        </template>

        <!-- Rechte Seite: Suchfeld, Filialauswahl und Profil-Menü -->
        <template #end>
            <!-- Suchfeld mit Fehleranzeige -->
            <div class="relative p-menubar-suche">
                <InputGroup>
                    <InputText v-model="suchText" type="text" :placeholder="t('[AppTopbar]SchnellsuchePlaceholder')" @keyup="handleSuchEnter" />
                    <Button icon="pi pi-search" outlined severity="secondary" @click="schnellsuche" />
                </InputGroup>
            </div>

            <Button v-for="sprache in verfuegbareSprachen" class="p-button-rounded p-button-text sprachauswahl">
                <template #icon>
                    <img :src="getAbsoluteSpaUrl(`images/flagge.${sprache}.svg`)" @click="spracheWechseln(sprache)" />
                </template>
            </Button>

            <!-- Filialauswahl - nur anzeigen, wenn Filialen vorhanden sind -->
            <FilialAuswahl />

            <!-- Profil-Menü -->
            <Menu ref="menu" :model="profileMenuItems" :popup="true" />
            <Button icon="pi pi-user" class="p-button-rounded p-button-text" @click="toggleProfileMenu" />
        </template>
    </Menubar>
</template>
